import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonChevronContent} from '../../../../libs/shared/ui/src/components/buttons/buttonChevronContent';
import {ButtonChrome} from '../../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {growToFitHeightStyle} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {isDarkTheme} from '../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {navigateToConversation} from '../../../store/stateless/navigationActions';
import {FlexSpacer} from '../../common/flex';
import {EducationTooltipCoordinator} from '../../common/tooltips/educationTooltip/educationTooltipCoordinator';
import {MessageBase} from '../viewer/activity/message/messageBase';
import {ComposerBase} from './composerBase';

interface DisabledComposerPlaceholder {
  timelineVariation: TimelineVariationsEnum;
  targetConversationId: number;
}

const StyledEditorDiv = styled.div`
  ${growToFitHeightStyle};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-auto-rows: 100%;
  background-color: ${(p) => computeBackgroundColor(p.theme)};
  border-radius: 18px 18px 0 0;
`;

const StyledContentEditableWrapperDiv = styled.div`
  max-height: 15vh;
  padding: 15px 16px;
`;

const StyledIconContainerDiv = styled.div`
  padding: 4px 8px;
`;

const StyledToolbarDiv = styled.div`
  flex-shrink: 0;

  display: flex;
  padding: 2px 8px 8px;

  background-color: ${(p) => computeBackgroundColor(p.theme)};
  border-radius: 0 0 16px 16px;
`;

function computeBackgroundColor(theme: UserTheme) {
  if (isDarkTheme(theme)) {
    return theme.greys.shade30;
  }

  return theme.greys.shade10;
}

const StyledPlaceholderDiv = styled.div`
  display: flex;
  color: ${(p) => p.theme.greys.shade60};
  flex-direction: row;
  gap: 4px;
`;

const StyledButtonDiv = styled.div`
  margin: 0 3px;
`;

const StyledChevronButton = styled(ChromelessButton)`
  display: flex;
  align-items: stretch;
`;

const StyledComposerDiv = styled.div`
  padding-left: 5px;
  padding-right: 15px;
  margin-top: 15px;
`;

export const DisabledChatComposerPlaceholder: FC<DisabledComposerPlaceholder> = (props) => {
  const {timelineVariation, targetConversationId} = props;
  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const renderEditor = () => (
    <StyledEditorDiv>
      <StyledContentEditableWrapperDiv>
        <StyledPlaceholderDiv>
          <Trans id="draft-disabled-placeholder-splitorigin">
            Cannot reply because this conversation was split
          </Trans>
          <EducationTooltipCoordinator
            title={<Trans id="draft-disabled-tooltip-title-splitorigin">Conversation split</Trans>}
            tooltip={
              <Trans id="draft-disabled-tooltip-text-splitorigin">
                Messages have been split into a new conversation. You can only reply in the newest
                conversation with these recipients.
              </Trans>
            }
            buttons={[
              <Button
                size={VisualSizesEnum.SMALL}
                onClick={() => {
                  dispatch(navigateToConversation(targetConversationId));
                }}
              >
                <Trans id="draft-disabled-tooltip-button-splitorigin">Go to conversation</Trans>
              </Button>,
            ]}
            isInline
          />
        </StyledPlaceholderDiv>
      </StyledContentEditableWrapperDiv>
    </StyledEditorDiv>
  );

  const toolbarIcons: ReadonlyArray<IconName> = [
    'emoji',
    'attachment',
    'messageTemplate',
    'knowledgeBase',
    'integration',
  ];

  const renderSendButton = () => (
    <StyledButtonDiv>
      <ButtonChrome variant={ButtonVariantsEnum.PRIMARY} isDisabled>
        {(buttonProps) => (
          <>
            <ChromelessButton {...buttonProps}>
              <ButtonContent {...buttonProps}>
                <Icon name="sendFilledSmall" isDisabled />
              </ButtonContent>
            </ChromelessButton>
            <StyledChevronButton {...buttonProps}>
              <ButtonChevronContent {...buttonProps}>
                <Icon name="chevronDownSmall" />
              </ButtonChevronContent>
            </StyledChevronButton>
          </>
        )}
      </ButtonChrome>
    </StyledButtonDiv>
  );

  const renderBottomBar = (_: boolean) => (
    <StyledToolbarDiv>
      {toolbarIcons.map((iconName) => (
        <StyledIconContainerDiv>
          <Icon name={iconName} color={theme.greys.shade60} size={20} />
        </StyledIconContainerDiv>
      ))}

      <FlexSpacer />
      {renderSendButton()}
    </StyledToolbarDiv>
  );

  return (
    <StyledComposerDiv>
      <MessageBase timelineVariation={timelineVariation}>
        <ComposerBase
          header={null}
          editor={renderEditor()}
          toolbar={renderBottomBar}
          onDropAttachmentItems={() => {}}
          onDropFile={() => {}}
        />
      </MessageBase>
    </StyledComposerDiv>
  );
};
